// API de Worktime Service para Vue 3
// Migrado desde worktime.js
// Proporciona acceso tipado al backend REST

import type { AxiosError, AxiosRequestConfig } from "axios";
import discovery from "../discovery";
import { HTTP, UTILS } from "../http-common";
import type { FullActivityUpdateDetailDTO } from "./activityDetailDTO";
import type {
  SurveySubmitResponseDTO,
  SurveyViewerInfoDTO,
} from "./surveyDTO";
import type {
  EmployeeSettingsDTO,
  GenericServiceResponseDTO,
  ProfileEmployeeDTO,
  ProfilePictureUploadResponseDTO,
  WhatsAppStatusResponseDTO,
  WhatsAppVerifyOtpRequestDTO,
} from "./profileDTO";
import type { FullTargetHoursDTO } from "./targetHoursDTO";

/**
 * Callbacks genéricos
 */
type SuccessCallback<T = any> = (data: T) => void;
type ErrorCallback = (error: AxiosError) => void;

/**
 * API cliente para comunicación con Worktime Service
 */
export class WorktimeApi {
  /**
   * Obtiene los headers comunes para todas las peticiones
   * Público para permitir que APIs modulares lo reutilicen
   */
  public getCommonHeaders(): AxiosRequestConfig {
    return {};
  }

  /**
   * GET genérico
   */
  genericGet<T = any>(
    service: string,
    onSuccess: SuccessCallback<T>,
    onError: ErrorCallback
  ): void {
    HTTP.get(
      `${discovery.worktime_service_url}${service}`,
      this.getCommonHeaders()
    )
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        onError(error);
      });
  }

  /**
   * POST genérico
   */
  genericPost<T = any>(
    service: string,
    data: any,
    onSuccess: SuccessCallback<T>,
    onError: ErrorCallback
  ): void {
    const config = this.getCommonHeaders();

    // Si es FormData, dejar que el navegador establezca el Content-Type automáticamente
    // (incluye el boundary necesario para multipart/form-data)
    if (data instanceof FormData) {
      // No establecer Content-Type, axios lo hará automáticamente
      // El navegador añadirá: Content-Type: multipart/form-data; boundary=----WebKitFormBoundary...
    }

    HTTP.post(`${discovery.worktime_service_url}${service}`, data, config)
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        onError(error);
      });
  }

  /**
   * POST JSON explícito para payloads primitivos o casos donde el backend
   * necesita `application/json` aunque axios no lo infiera automáticamente.
   */
  genericJsonPost<T = any>(
    service: string,
    data: any,
    onSuccess: SuccessCallback<T>,
    onError: ErrorCallback
  ): void {
    const config = this.getCommonHeaders();

    if (!config.headers) {
      config.headers = {};
    }

    config.headers["Content-Type"] = "application/json";

    HTTP.post(
      `${discovery.worktime_service_url}${service}`,
      JSON.stringify(data),
      config
    )
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        onError(error);
      });
  }

  /**
   * PUT genérico
   */
  genericPut<T = any>(
    service: string,
    data: any,
    onSuccess?: SuccessCallback<T>,
    onError?: ErrorCallback
  ): void {
    HTTP.put(
      `${discovery.worktime_service_url}${service}`,
      data,
      this.getCommonHeaders()
    )
      .then((response) => {
        if (onSuccess) onSuccess(response.data);
      })
      .catch((error) => {
        if (onError) onError(error);
      });
  }

  /**
   * DELETE genérico
   */
  genericDelete<T = any>(
    service: string,
    onSuccess: SuccessCallback<T>,
    onError: ErrorCallback
  ): void {
    HTTP.delete(
      `${discovery.worktime_service_url}${service}`,
      this.getCommonHeaders()
    )
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        onError(error);
      });
  }

  /**
   * Obtiene un valor del contexto
   */
  getContextValue(key: string): string {
    const context = (window as any).tweemContext;
    return context ? context[key] : "";
  }

  // ============================================
  // MÉTODOS COMUNES
  // ============================================

  /**
   * Ejecuta una query de analytics
   */
  executeQuery(
    data: any,
    onSuccess: SuccessCallback,
    onError: ErrorCallback
  ): void {
    this.genericPost("analytics/executeQuery", data, onSuccess, onError);
  }

  /**
   * Obtiene las empresas
   */
  getCompanies(onSuccess: SuccessCallback, onError: ErrorCallback): void {
    this.genericGet("admin/companies", onSuccess, onError);
  }

  getTrainingCatalog(onSuccess: SuccessCallback, onError: ErrorCallback): void {
    this.genericGet("user/training/catalog", onSuccess, onError);
  }

  getTrainingAction(
    actionId: number,
    onSuccess: SuccessCallback,
    onError: ErrorCallback
  ): void {
    this.genericGet(`user/training/action/${actionId}`, onSuccess, onError);
  }

  getMyTrainingEnrollments(
    onSuccess: SuccessCallback,
    onError: ErrorCallback
  ): void {
    this.genericGet("user/training/enrollments", onSuccess, onError);
  }

  getMyTrainingProgress(
    onSuccess: SuccessCallback,
    onError: ErrorCallback
  ): void {
    this.genericGet("user/training/progress", onSuccess, onError);
  }

  getTargetHoursList(
    onSuccess: SuccessCallback<FullTargetHoursDTO[]>,
    onError: ErrorCallback
  ): void {
    this.genericGet("user/targetHoursList", onSuccess, onError);
  }

  getActivityDetail(
    activityId: number,
    onSuccess: SuccessCallback<FullActivityUpdateDetailDTO>,
    onError: ErrorCallback
  ): void {
    this.genericGet(`user/activity/${activityId}/detail`, onSuccess, onError);
  }

  getFormSurveyInfo(
    surveyId: number,
    onSuccess: SuccessCallback<SurveyViewerInfoDTO>,
    onError: ErrorCallback
  ): void {
    this.genericGet(`user/survey/form/${surveyId}`, onSuccess, onError);
  }

  storeFormSurveySubmission(
    surveyId: number,
    data: any,
    onSuccess: SuccessCallback<SurveySubmitResponseDTO>,
    onError: ErrorCallback
  ): void {
    this.genericPost(`user/survey/form/submission/${surveyId}`, data, onSuccess, onError);
  }

  getMyTeam(
    onSuccess: SuccessCallback<ProfileEmployeeDTO[]>,
    onError: ErrorCallback
  ): void {
    this.genericGet("user/getMyTeam", onSuccess, onError);
  }

  getWhatsAppStatus(
    onSuccess: SuccessCallback<WhatsAppStatusResponseDTO>,
    onError: ErrorCallback
  ): void {
    this.genericGet("user/whatsapp/status", onSuccess, onError);
  }

  requestWhatsAppOtp(
    onSuccess: SuccessCallback<GenericServiceResponseDTO>,
    onError: ErrorCallback
  ): void {
    this.genericPost("user/whatsapp/requestOtp", null, onSuccess, onError);
  }

  verifyWhatsAppOtp(
    data: WhatsAppVerifyOtpRequestDTO,
    onSuccess: SuccessCallback<WhatsAppStatusResponseDTO>,
    onError: ErrorCallback
  ): void {
    this.genericPost("user/whatsapp/verifyOtp", data, onSuccess, onError);
  }

  disconnectWhatsApp(
    onSuccess: SuccessCallback<GenericServiceResponseDTO>,
    onError: ErrorCallback
  ): void {
    this.genericPost("user/whatsapp/disconnect", null, onSuccess, onError);
  }

  updateNotifications(
    type: number,
    enable: boolean,
    onSuccess: SuccessCallback<void>,
    onError: ErrorCallback
  ): void {
    this.genericPut(`user/updateNotifications/${type}/${enable}`, null, onSuccess, onError);
  }

  updateRegisterKey(
    registerKey: string,
    onSuccess: SuccessCallback<void>,
    onError: ErrorCallback
  ): void {
    this.genericPut("user/updateRegisterKey", registerKey, onSuccess, onError);
  }

  updateEmployeeSettings(
    data: EmployeeSettingsDTO,
    onSuccess: SuccessCallback<EmployeeSettingsDTO>,
    onError: ErrorCallback
  ): void {
    this.genericPut("user/employee/settings", data, onSuccess, onError);
  }

  uploadProfilePicture(
    file: File,
    onSuccess: SuccessCallback<ProfilePictureUploadResponseDTO>,
    onError: ErrorCallback,
    onProgress?: (progress: number) => void
  ): void {
    const formData = new FormData();
    formData.append("file", file);

    HTTP.post<ProfilePictureUploadResponseDTO>(
      `${discovery.worktime_service_url}user/profilePicture`,
      formData,
      {
        ...this.getCommonHeaders(),
        onUploadProgress: (event) => {
          if (!onProgress || !event.total) {
            return;
          }

          onProgress(Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100))));
        },
      }
    )
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        onError(error);
      });
  }

  enrollTrainingAction(
    data: any,
    onSuccess: SuccessCallback,
    onError: ErrorCallback
  ): void {
    this.genericPost("user/training/enroll", data, onSuccess, onError);
  }

  /**
   * Obtiene los centros de trabajo por ID de empresa
   */
  getWorkplacesByCompanyId(
    companyId: number,
    onSuccess: SuccessCallback,
    onError: ErrorCallback
  ): void {
    this.genericGet(
      `admin/workplaces-by-company-id/${companyId}`,
      onSuccess,
      onError
    );
  }

  // ============================================
  // UTILIDADES
  // ============================================

  /**
   * Descarga un documento
   */
  downloadDocument = UTILS.downloadDocument;
}

// Exportar instancia única (singleton)
export const worktimeApi = new WorktimeApi();

// Exportar como default para compatibilidad
export default worktimeApi;
